import type { WrittenPage } from '../sections'
import { LINKS } from './links'

/**
 * The "Importance of Measurement for Transformation Success" e-book's own page.
 *
 * Built to the design of the digitopia.co page it replaces
 * (`/impact-labs/ebooks/importance-of-measurement-for-transformation-success/`),
 * sent as four screenshots on 2026-08-28 with *"referans alarak ilgili sayfayı
 * tastamam şekilde, aynısını, tıpkısını tasarla"*. Section for section that
 * page's: the two-column hero with the cover artwork beside it, the grey "Why
 * Download?" card with the cover pair and two paragraphs, the "What You'll
 * Learn" card with the stacked copies and six bullets, and the centred download
 * ask at the end. The copy is that page's copy, verbatim.
 *
 * Fourth e-book page after Transform Better, Agentic AI and Sustainability
 * Innovation, and it follows `ebook-sustainability-innovation.ts` in every place
 * the reference and this site disagree — the same three deliberate departures,
 * for the same reasons:
 *
 *  * **The eyebrow is the site's own**, centred over the copy column, rather
 *    than digitopia.co's small left-pinned "WHAT GETS MEASURED GETS DONE"
 *    label. Every other hero here is that shape.
 *  * **The download is a direct link to the PDF, not a gated form.** The
 *    reference puts a nine-field HubSpot form between the reader and the file;
 *    no form on this site submits anywhere yet, so reproducing it would mean
 *    the page's only call to action silently drops every lead. The `check`
 *    below says so, as the sibling pages' do.
 *  * **A testimonial closes the page**, which the reference does not have — it
 *    is what the sibling e-book pages do, asked for again on 2026-08-28
 *    (*"en aşağıda önceden konuştuğumuz gibi testimonial template ini
 *    kullanalım"*). The card is TEMSA's: of the twenty in
 *    `content/testimonials.ts` it is the one about **measurement** — an index
 *    that gave them an objective view of where they stand, let them track
 *    progress over time and tied investment to measurable outcomes, which is
 *    this book's whole argument. Its words are the Hall of Fame's; that file is
 *    where a change to them belongs.
 *
 * **The slug is confirmed, and it is not the one the catalogue derived.**
 * `content/ebooks.ts` guessed `whats-measured-gets-done` from the part of the
 * title before the colon; the screenshots came from
 * `digitopia.co/impact-labs/ebooks/importance-of-measurement-for-transformation-success/`,
 * so the real address is the long one. The catalogue entry was corrected to
 * match, which also closed that e-book's slug `check` — the second time the
 * title rule has been caught out, after Sustainability Innovation.
 */
export const ebookMeasurement: WrittenPage = {
  navLabel: 'Measurement for Transformation',
  title: 'The Importance of Measurement for Transformation Success',
  description:
    'What gets measured gets done: how structured measurement turns transformation from aspiration into achievement. Download the e-book from Digitopia Impact Labs.',
  sections: [
    {
      kind: 'hero',
      eyebrow: 'What Gets Measured Gets Done',
      headline: 'The Importance of Measurement for Transformation Success',
      body: [
        'Discover how structured measurement can turn your transformation from aspiration to '
          + 'achievement.',
      ],
      ctas: [{ label: 'Download', href: LINKS.measurementEbookPdf }],
      photo: {
        src: '/photo/measurement-transformation-ebook-artwork.webp',
        alt:
          'The e-book’s cover artwork: a glass pocket watch on black, its dial a galaxy ringed '
          + 'by orbiting gold arcs.',
      },
      // The artwork is square and the site's hero box is 3:2, which would take
      // the top and bottom off the watch.
      photoFrame: 'square',
      /*
        Clause 1 — THE DOWNLOAD IS NOT GATED — resolved 2026-08-30. It said no
        form on this site submits anywhere and the Send button is disabled on
        purpose. Both stopped being true today: the form posts to
        `website-form-lead`, the submission opens a task on its page's own
        counterpart, and every Download here carries `data-gated-download`, so
        the reader is asked for an email and the PDF is sent to it. No HubSpot
        form id is needed for any of it. What is left is clause 2.
      */
      check:
        'THE COVER ARTWORK IS 768×768. It is the largest copy of it anybody '
        + 'has sent; every other hero on the site is 2560px, and this one is painted '
        + 'at roughly its own width on a laptop with nothing left over for a retina '
        + 'screen. It is generated art rather than a photograph, so a bigger export '
        + 'should exist wherever it was made:\n'
        + '     node apps/website/scripts/attach.mjs <file> photo-src/measurement-transformation-ebook-artwork.webp\n'
        + '     pnpm --filter @digitopia/website images\n'
        + '   and nothing in this file changes. The same is true of the two mockups '
        + 'below it (900px wide, both), which are drawn smaller and are therefore '
        + 'less pressing.',
    },
    {
      kind: 'cta',
      tone: 'card',
      // Mockup left, offer right — the reference's order, and it alternates with
      // the card under it.
      flip: true,
      heading: 'Why Download?',
      body: [
        'Transformation isn’t a one-time initiative; it’s your organization’s new operating '
          + 'system. Yet most companies launch change programs without the one thing they need '
          + 'most: visibility. This ebook reveals why measurement is the single most powerful '
          + 'tool for ensuring your transformation actually works.',
        'Whether you’re leading digital, cultural, or sustainability efforts, you’ll learn how '
          + 'to avoid wasted effort, align your teams, and move with purpose rather than merely '
          + 'motion.',
      ],
      ctas: [{ label: 'Download the Ebook', href: LINKS.measurementEbookPdf }],
      photo: {
        src: '/photo/measurement-transformation-ebook-covers.webp',
        alt:
          'Two copies of the measurement e-book, one behind the other, on a pale gradient.',
      },
    },
    {
      kind: 'cta',
      tone: 'card',
      heading: 'What You’ll Learn',
      points: [
        'Why most transformation efforts stall, and how measurement breaks the cycle',
        'How to connect maturity, capability, and outcomes for real business results',
        'When and how to use benchmarking to sharpen your strategy',
        'How to build a common language across leadership, teams, and functions',
        'Why continuous improvement depends on continuous measurement',
        'Real-world case studies from leaders in retail, logistics, and more',
      ],
      ctas: [{ label: 'Download the Ebook', href: LINKS.measurementEbookPdf }],
      photo: {
        src: '/photo/measurement-transformation-ebook-stack.webp',
        alt: 'A stack of printed copies of the measurement e-book seen from above.',
      },
    },
    {
      kind: 'prose',
      align: 'center',
      heading: 'Ready to Make Progress Visible?',
      sub:
        'Download the full guide to learn how measurement drives successful transformation, from '
        + 'strategy to culture to ROI.',
      cta: { label: 'Download the Ebook', href: LINKS.measurementEbookPdf },
    },
    {
      kind: 'quotes',
      variant: 'fame',
      heading: 'What our customers say.',
      items: [
        {
          // The Hall of Fame's TEMSA card, word for word — `content/testimonials.ts`
          // is where these words are edited, and the Turkish page carries its own
          // saying of them.
          quote:
            'Digitopia’s Digital Maturity Index has been a key element in structuring our '
            + 'digital transformation. It provided us with a clear, objective view of where we '
            + 'stand and how we compare—both within our holding and across the industry. More '
            + 'importantly, it enabled us to track progress over time and align our digital '
            + 'investments with measurable outcomes. This structured approach brings clarity, '
            + 'focus, and confidence to our transformation efforts.',
          source: 'Erdinç Uğurlu, CIO',
          company: 'TEMSA',
          logo: { name: 'TEMSA', src: '/logo/temsacard.png' },
          portrait: { src: '/photo/portrait-erdinc-ugurlu.png', alt: 'Erdinç Uğurlu' },
        },
      ],
    },
  ],
}
